import { PrefName } from '@bp/bp-graphql-types';
import {
  Checkbox,
  Grid,
  GridColumn,
  GridRow,
  MailIcon,
  MobilePhoneIcon,
  NotificationDefaultIcon,
} from '@bp/ui-components';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import type { ProfileNotificationPreferencesConnectFieldInput } from 'client/gql/graphql';
import { Form, Formik } from 'formik';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useTranslation } from 'react-i18next';
import { type CombinedError, useMutation } from 'urql';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { showErrorToast } from '../../utils/showErrorToast';
import { showSuccessToast } from '../../utils/showSuccessToast';
import { ModalBottomButtons } from '../ModalBottomButtons/ModalBottomButtons';
import styles from './NotificationSettingsForm.module.scss';
import { type NotificationTypes } from './types';

const CreateNotificationPreferencesMutationDocument = graphql(`
  mutation CreateNotificationPreferences($input: [PreferenceCreateInput!]!) {
    createPreferences(input: $input) {
      info {
        nodesCreated
      }
    }
  }
`);
const UpdateProfileMutationDocument = graphql(`
  mutation UpdateProfile($uuid: ID!, $update: ProfileUpdateInput!) {
    updateProfiles(where: { uuid: $uuid }, update: $update) {
      profiles {
        __typename
        prefixName
        displayName
        selectName
        displayNameShort
        organizationRoles
        firstName
        firstNames
        lastName
        uuid
        title
        salutation
          user {
              __typename
              uuid
              status
          }
        notificationPreferencesConnection {
          __typename
          edges {
            __typename
            node {
              __typename
              name
            }
            properties {
              __typename
              transports
            }
          }
        }
        organization {
          uuid
        }
      }
    }
  }
`);

const NotificationSettingsFormFieldsDocument = graphql(`
  fragment NotificationSettingsFormFields on Query {
    profiles(where: $profilesWhere, options: $profilesOptions) {
      uuid
      __typename
      notificationPreferencesConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            name
          }
          properties {
            __typename
            transports
          }
        }
      }
    }
  }
`);

type NotificationSettingsFormProps = {
  query: FragmentType<typeof NotificationSettingsFormFieldsDocument> | undefined;
};
export const NotificationSettingsForm = ({ query }: NotificationSettingsFormProps) => {
  const { t } = useTranslation();
  const pimAuthClaims = useAuthClaims().pimAuthClaims;

  const fragment = getFragmentData(NotificationSettingsFormFieldsDocument, query);
  const data =
    fragment?.profiles[0]?.notificationPreferencesConnection.edges.map((e) => ({
      name: e.node.name ?? '',
      value: e.properties.transports as NotificationTypes[],
    })) ?? [];

  const perms = usePermissionChecker();

  const [, createPreferences] = useMutation(CreateNotificationPreferencesMutationDocument);

  function getServices(name: PrefName): NotificationTypes[] {
    return data?.find((n) => n.name === name)?.value ?? ['bell', 'mail'];
  }

  const initialValues: Record<PrefName, NotificationTypes[]> = {
    [PrefName.AssignmentOpened]: getServices(PrefName.AssignmentOpened),
    [PrefName.AssignmentUpdated]: getServices(PrefName.AssignmentUpdated),
    [PrefName.AssignmentDeleted]: getServices(PrefName.AssignmentDeleted),
    [PrefName.SubmissionNew]: getServices(PrefName.SubmissionNew),
    [PrefName.SubmissionUpdated]: getServices(PrefName.SubmissionUpdated),
    [PrefName.NewMaterial]: getServices(PrefName.NewMaterial),
    [PrefName.MaterialUpdated]: getServices(PrefName.MaterialUpdated),
    [PrefName.MaterialDeleted]: getServices(PrefName.MaterialDeleted),
    [PrefName.NewMessage]: getServices(PrefName.NewMessage),
    [PrefName.NewAnnouncement]: getServices(PrefName.NewAnnouncement),
    [PrefName.NewGroupMembership]: getServices(PrefName.NewGroupMembership),
    [PrefName.NewCourseMembership]: getServices(PrefName.NewCourseMembership),
    [PrefName.ConferenceStarted]: getServices(PrefName.ConferenceStarted),
    [PrefName.NewAppointment]: getServices(PrefName.NewAppointment),
    [PrefName.NewCollaboratingCourseOrGroup]: getServices(PrefName.NewCollaboratingCourseOrGroup),
  };

  const checkboxes = [
    {
      group: t('assignments.title', { count: 2 }),
      boxes: [
        {
          channel: PrefName.AssignmentOpened,
          label: t('notificationSettings.newAssignment'),
        },
        {
          channel: PrefName.AssignmentUpdated,
          label: t('notificationSettings.assignmentUpdated'),
        },
        {
          channel: PrefName.AssignmentDeleted,
          label: t('notificationSettings.assignmentDeleted'),
        },
        {
          channel: PrefName.SubmissionNew,
          label: t('notificationSettings.newSubmission'),
        },
        {
          channel: PrefName.SubmissionUpdated,
          label: t('notificationSettings.submissionUpdated'),
        },
      ],
    },
    {
      group: t('workmaterials.title'),
      boxes: [
        {
          channel: PrefName.NewMaterial,
          label: t('notificationSettings.newMaterial'),
        },
        {
          channel: PrefName.MaterialUpdated,
          label: t('notificationSettings.materialUpdated'),
        },
        {
          channel: PrefName.MaterialDeleted,
          label: t('notificationSettings.materialDeleted'),
        },
      ],
    },
    {
      group: t('messages.title', { count: 2 }),
      boxes: [
        {
          channel: PrefName.NewMessage,
          label: t('notificationSettings.newMessage'),
        },
        {
          channel: PrefName.NewAnnouncement,
          label: t('notificationSettings.newAnnouncement'),
        },
      ],
    },
    {
      group: t('common.miscellaneous'),
      boxes: [
        {
          channel: PrefName.NewCourseMembership,
          label: t('notificationSettings.newCourseMembership'),
        },
        {
          channel: PrefName.NewGroupMembership,
          label: t('notificationSettings.newGroupMembership'),
        },
        {
          channel: PrefName.ConferenceStarted,
          label: t('notificationSettings.conferenceStarted'),
        },
        {
          channel: PrefName.NewAppointment,
          label: t('notificationSettings.newAppointment'),
        },
        {
          channel: PrefName.NewCollaboratingCourseOrGroup,
          label: t('notificationSettings.collabCourseOrGroupCreated'),
        },
      ],
    },
  ];

  const [, updateProfile] = useMutation(UpdateProfileMutationDocument);

  const onSubmit = async (values: typeof initialValues) => {
    if (perms?.canUpdateProfile(pimAuthClaims.getProfile())) {
      const existingPreferences = data?.map((p) => p.name);
      const missingPreferences = Object.keys(values).filter((name) => !existingPreferences?.includes(name as PrefName));
      if (missingPreferences.length > 0) {
        await createPreferences({
          input: missingPreferences.map((name) => ({ name: name as PrefName })),
        });
      }

      const payload: ProfileNotificationPreferencesConnectFieldInput[] = Object.entries(values).map(
        ([name, value]) => ({
          overwrite: true,
          where: { node: { name: name as PrefName } },
          edge: { transports: value },
        }),
      );
      const resp = await updateProfile({
        uuid: pimAuthClaims.getProfile().uuid,
        update: {
          notificationPreferences: [{ connect: payload }],
        },
      });
      if (resp.error) {
        showErrorToast(resp.error);
      } else {
        showSuccessToast(t('common.saved'));
      }
    }

    if (!perms?.canUpdateProfile(pimAuthClaims.getProfile())) {
      showErrorToast({ message: t('common.notAllowed') } as CombinedError);
    }
  };

  return (
    <Formik<typeof initialValues> initialValues={initialValues} onSubmit={onSubmit}>
      {({ resetForm, setFieldValue, values, isSubmitting, errors }) => {
        const handleClick = async (key: keyof typeof initialValues, channel: NotificationTypes) => {
          const newValue = values[key];
          const index = newValue.indexOf(channel);
          if (index !== -1) {
            newValue.splice(index, 1);
          } else {
            newValue.push(channel);
          }
          await setFieldValue(key, newValue);
        };

        const handleSelect = async (select: boolean) => {
          for (const key in initialValues) {
            let value = values[key as keyof typeof values] as Array<NotificationTypes>;
            value = [];
            if (select) value = ['bell', 'push', 'mail'];
            await setFieldValue(key, value);
          }
        };

        return (
          <Form>
            <Grid className={styles['notifications-settings-form']}>
              <GridRow mobileGap='var(--grid-column-gap)' className={styles['grid-row']}>
                {checkboxes.map((c, i) => (
                  <GridColumn key={i} width={3}>
                    <div className={styles.header}>
                      <NotificationDefaultIcon />
                      <MobilePhoneIcon />
                      <MailIcon />
                      <span className={styles.group}>{c.group}</span>
                    </div>
                    {c.boxes.map((b, i) => {
                      return (
                        <div key={i} className={styles.boxes}>
                          <Checkbox
                            className={styles.bell}
                            checked={values[b.channel]?.includes('bell')}
                            name={`${b.channel}_bell`}
                            onChange={() => handleClick(b.channel, 'bell')}
                          />
                          <Checkbox
                            disabled
                            checked={false}
                            // checked={values[b.channel]?.includes('push')}
                            className={styles.push}
                            name={`${b.channel}_push`}
                            onChange={() => handleClick(b.channel, 'push')}
                          />
                          <Checkbox
                            className={styles.mail}
                            checked={values[b.channel]?.includes('mail')}
                            name={`${b.channel}_mail`}
                            onChange={() => handleClick(b.channel, 'mail')}
                          />
                          <div className={styles.label}>{b.label}</div>
                        </div>
                      );
                    })}
                  </GridColumn>
                ))}
              </GridRow>
            </Grid>
            <ModalBottomButtons
              errors={errors}
              closeButton={{ callback: () => resetForm() }}
              isLoading={isSubmitting}
              additionalButtons={[
                {
                  text: t('common.selectAll'),
                  hierarchy: 'tertiary',
                  callback: () => handleSelect(true),
                },
                {
                  text: t('notifications.deselectAll'),
                  hierarchy: 'tertiary',
                  callback: () => handleSelect(false),
                },
              ]}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
